import { useCallback, useEffect, useRef, useState } from 'react';
import Point from '@arcgis/core/geometry/Point';
import Polygon from '@arcgis/core/geometry/Polygon';
import Polyline from '@arcgis/core/geometry/Polyline';
import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import type { TakEntity } from 'hooks/takEntity/types';
import type { ArcgisMapView } from './arcgisView';

const TAK_LAYER_ID = 'tak-layer';

// CoT affiliation is the 3rd character of atom types ('a-f-...' = friendly,
// 'a-h-...' = hostile, 'a-n-...' = neutral, otherwise unknown).
const AFFILIATION_COLORS: Record<string, [number, number, number, number]> = {
  f: [37, 99, 235, 1],
  h: [220, 38, 38, 1],
  n: [22, 163, 74, 1],
};
const UNKNOWN_COLOR: [number, number, number, number] = [202, 138, 4, 1];
const STALE_COLOR: [number, number, number, number] = [107, 114, 128, 0.6];
const SHAPE_FILL: [number, number, number, number] = [37, 99, 235, 0.15];

function isStale(entity: TakEntity): boolean {
  return new Date(entity.staleAt).getTime() < Date.now();
}

function colorForEntity(entity: TakEntity): [number, number, number, number] {
  if (isStale(entity)) return STALE_COLOR;
  const affiliation = entity.cotType.split('-')[1];
  return AFFILIATION_COLORS[affiliation] ?? UNKNOWN_COLOR;
}

function pointSymbol(entity: TakEntity): SimpleMarkerSymbol {
  const color = colorForEntity(entity);

  if (entity.category === 'track') {
    return new SimpleMarkerSymbol({
      style: 'triangle',
      color,
      size: 14,
      angle: entity.course ?? 0,
      outline: { color: [255, 255, 255, 0.9], width: 1 },
    });
  }

  return new SimpleMarkerSymbol({
    style: 'diamond',
    color,
    size: 12,
    outline: { color: [255, 255, 255, 0.9], width: 1 },
  });
}

function shapeGraphics(entity: TakEntity): Graphic[] {
  const geometry = entity.geometryGeojson;
  if (!geometry) return [];

  const color = colorForEntity(entity);
  const attributes = { takUid: entity.uid };

  if (geometry.type === 'Polygon' || geometry.type === 'MultiPolygon') {
    const rings =
      geometry.type === 'Polygon'
        ? geometry.coordinates
        : geometry.coordinates.flatMap((poly) => poly);
    return [
      new Graphic({
        geometry: new Polygon({ rings }),
        symbol: new SimpleFillSymbol({
          color: SHAPE_FILL,
          outline: { color, width: 2 },
        }),
        attributes,
      }),
    ];
  }

  if (geometry.type === 'LineString' || geometry.type === 'MultiLineString') {
    const paths =
      geometry.type === 'LineString'
        ? [geometry.coordinates]
        : geometry.coordinates;
    return [
      new Graphic({
        geometry: new Polyline({ paths }),
        symbol: new SimpleLineSymbol({ color, width: 2, style: 'dash' }),
        attributes,
      }),
    ];
  }

  return [];
}

function popupHtml(entity: TakEntity): HTMLDivElement {
  const lastSeen = new Date(entity.updatedAt).toLocaleString();
  const container = document.createElement('div');

  const rows: Array<[string, string]> = [
    ['Type', entity.cotType],
    ['Category', entity.category],
    ['Last seen', lastSeen],
  ];
  if (entity.speed !== null)
    rows.push(['Speed', `${entity.speed.toFixed(1)} m/s`]);
  if (entity.course !== null)
    rows.push(['Course', `${Math.round(entity.course)}°`]);
  if (isStale(entity)) rows.push(['Status', 'STALE']);

  for (const [label, value] of rows) {
    const row = document.createElement('div');
    const labelEl = document.createElement('strong');
    labelEl.textContent = `${label}: `;
    row.appendChild(labelEl);
    row.appendChild(document.createTextNode(value));
    container.appendChild(row);
  }

  return container;
}

/**
 * Renders TAK entities (from the shared DB, ingested by the pratus-tak daemon)
 * as a graphics layer on the USCG map.
 *
 * Note: useIncidentLayerSync has the same click-handler-per-poll churn this
 * hook avoids via entitiesRef; fix it there the same way when touched.
 */
export const useTakLayerSync = ({
  mapView,
  takEntities,
  isVisible,
  suppressMapClicks = false,
}: {
  mapView: ArcgisMapView | null;
  takEntities: TakEntity[];
  isVisible: boolean;
  /** When the AI select tool is on, leave the click for layer/feature pick. */
  suppressMapClicks?: boolean;
}) => {
  // The layer lives in a ref (it's an imperative ArcGIS object we mutate), but
  // creation flips `layerReady` state so the render effect re-runs — otherwise
  // warm react-query data would stay unrendered until the next poll when the
  // MapView arrives late (e.g. basemap switch recreates it).
  const layerRef = useRef<GraphicsLayer | null>(null);
  const [layerReady, setLayerReady] = useState(false);
  // The click handler reads entities through a ref so it can be registered
  // once per MapView instead of being torn down on every 15s poll.
  const entitiesRef = useRef<TakEntity[]>(takEntities);
  useEffect(() => {
    entitiesRef.current = takEntities;
  }, [takEntities]);

  useEffect(() => {
    if (!mapView?.map) return;

    const existingLayer = mapView.map.findLayerById(
      TAK_LAYER_ID
    ) as GraphicsLayer | null;

    const takLayer =
      existingLayer ?? new GraphicsLayer({ id: TAK_LAYER_ID, title: 'TAK' });
    if (!existingLayer) mapView.map.add(takLayer);
    layerRef.current = takLayer;
    setLayerReady(true);

    return () => {
      setLayerReady(false);
      layerRef.current = null;
      mapView.map?.remove(takLayer);
    };
  }, [mapView]);

  useEffect(() => {
    const layer = layerRef.current;
    if (!layerReady || !layer) return;

    layer.visible = isVisible;
    layer.removeAll();
    if (!isVisible) return;

    takEntities.forEach((entity) => {
      if (entity.category === 'shape') {
        const graphics = shapeGraphics(entity);
        if (graphics.length > 0) {
          graphics.forEach((graphic) => layer.add(graphic));
          return;
        }
        // Fall through: a shape without usable outline geometry still has its
        // CoT point — render it as a marker rather than vanishing.
      }

      layer.add(
        new Graphic({
          geometry: new Point({ longitude: entity.lon, latitude: entity.lat }),
          symbol: pointSymbol(entity),
          attributes: { takUid: entity.uid },
        })
      );
    });
  }, [layerReady, takEntities, isVisible]);

  const handleTakClick = useCallback(
    async (event: unknown) => {
      if (suppressMapClicks) return;
      const layer = layerRef.current;
      if (!layer || !mapView || !layer.visible) return;

      const hitResult = await mapView.hitTest(event as MouseEvent, {
        include: [layer],
      });

      const clickedGraphicHit = hitResult.results.find(
        (result) =>
          result.type === 'graphic' && result.graphic.layer?.id === TAK_LAYER_ID
      );

      if (!clickedGraphicHit || clickedGraphicHit.type !== 'graphic') return;

      const takUid = String(clickedGraphicHit.graphic.attributes?.takUid || '');
      const entity = entitiesRef.current.find((item) => item.uid === takUid);
      if (!entity) return;

      await mapView.openPopup({
        title: entity.callsign ?? entity.uid,
        location: new Point({ longitude: entity.lon, latitude: entity.lat }),
        content: popupHtml(entity),
      });
    },
    [mapView, suppressMapClicks]
  );

  useEffect(() => {
    if (!mapView) return;

    const clickHandle = mapView.on('click', handleTakClick);

    return () => {
      clickHandle.remove();
    };
  }, [mapView, handleTakClick]);
};
